import { UUID } from "@bitory-gmbh/nidavellir";
import { documentDbPut } from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Query,
  useDocumentDBGetAll,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import {
  useHtzPositionCatalogShowActiveCatalogQuery,
  useHtzPositionCatalogShowQuery,
  useLazyHtzPositionCatalogShowActiveCatalogQuery,
  useLazyHtzPositionCatalogShowQuery,
} from "@/services/backend/htz/position-catalog/api/position-catalog-api";
import { PositionCatalog } from "@/services/backend/htz/position-catalog/types/position-catalog";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

export function usePositionCatalogOffline(
  workOrderId: UUID,
  companyId: string,
) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [loadActive] = useLazyHtzPositionCatalogShowActiveCatalogQuery();
  const [loadCatalog] = useLazyHtzPositionCatalogShowQuery();

  const prepMsg = t("Speichere Leistungskatalog ...");
  const doneMsg = t("Leistungskatalog");

  const prepare = async () => {
    const activeResult = await loadActive({ companyId });
    if (activeResult.error) throw activeResult.error;
    if (!activeResult.data?.catalogId)
      throw new Error("No active catalog found");

    const catalogResult = await loadCatalog({
      id: activeResult.data.catalogId,
    });
    if (catalogResult.error) throw catalogResult.error;

    addStale("positionCatalog");
    setSynced((prev) => [...prev, prepMsg]);
    await documentDbPut(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "positionCatalog",
      catalogResult.data!,
    );
    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  return { prepare };
}

export function useGetPositionCatalog(
  workOrderId: string,
  companyId: string,
): Query<PositionCatalog> {
  const { offlineState } = useOfflineContext();

  const {
    data: activePositionCatalog,
    isLoading: activePositionCatalogIsLoading,
    error: activePositionCatalogError,
  } = useHtzPositionCatalogShowActiveCatalogQuery({
    companyId,
  });

  const {
    data: positionCatalog,
    isLoading: positionCatalogIsLoading,
    error: positionCatalogError,
  } = useHtzPositionCatalogShowQuery(
    {
      id: activePositionCatalog?.catalogId ?? "",
    },
    {
      skip:
        activePositionCatalog === undefined ||
        activePositionCatalog?.catalogId === null,
    },
  );

  const {
    data: indexedDBData,
    isLoading: indexedDBIsLoading,
    error: indexedDBError,
  } = useDocumentDBGetAll<PositionCatalog>(
    GSMDocumentKind.WorkOrder,
    workOrderId,
    "positionCatalog",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: positionCatalog,
        isLoading: activePositionCatalogIsLoading || positionCatalogIsLoading,
        error: activePositionCatalogError || positionCatalogError,
      };
    case OfflineState.OFFLINE:
      return {
        data: indexedDBData ? indexedDBData[0] : undefined,
        isLoading: indexedDBIsLoading,
        error: indexedDBError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: { status: 500 },
      };
  }
}
